// Shared signup for the PESO Panglao portal. Two paths:
//   - WITH email: client supabase.auth.signUp (the DB trigger creates the profile from
//     metadata, including registration_source + nsrp_data, so they survive email confirmation).
//   - WITHOUT email (phone-only): a small rate-limited backend endpoint admin-creates an
//     AUTO-CONFIRMED account using a phone-derived login handle, then we sign in client-side.
// Either way, once a session exists we PUT /api/profile to fill the typed columns the trigger
// doesn't map (skills, work_history, etc.) and upload any resume.
import { supabase } from '../../lib/supabase';
import api, { uploadResume } from '../../lib/api';
import { phoneToAuthEmail } from '../../lib/phoneAuth';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * @param {object} p
 * @param {string} [p.email]         optional — when blank, the phone number is the login
 * @param {string} p.password
 * @param {string} [p.phone]         used to derive the login handle when email is blank
 * @param {object} p.metadata        raw_user_meta_data for the trigger (must include role;
 *                                   may include registration_source + nsrp_data:<JSON string>)
 * @param {object} [p.typedProfile]  payload for PUT /api/profile (typed columns + nsrp_data object)
 * @param {File}   [p.resumeFile]    optional resume to attach after login
 * @returns {Promise<{needsEmailVerification: boolean}>}
 */
export async function pesoSignUp({ email, password, phone, metadata, typedProfile, resumeFile }) {
  const hasEmail = !!(email && email.trim());
  const authEmail = hasEmail ? email.trim() : phoneToAuthEmail(phone);
  if (!authEmail) throw new Error('A phone number or email is required to register.');

  let session = null;

  if (hasEmail) {
    const { data: authData, error } = await supabase.auth.signUp({
      email: authEmail,
      password,
      options: { data: metadata, emailRedirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) throw error;
    // Email-verification path: the trigger already persisted everything we put in metadata.
    // Nothing else to do until the user confirms + logs in.
    if (!authData?.session) {
      return { needsEmailVerification: true };
    }
    session = authData.session;
  } else {
    // Phone-only path: the backend creates an auto-confirmed account (admin API) so the
    // seeker can sign in immediately with their phone number — no email to confirm.
    await api.post('/api/peso/phone-signup', { phone, password, metadata });
    const { data: signInData, error: signInErr } = await supabase.auth.signInWithPassword({
      email: authEmail,
      password,
    });
    if (signInErr) throw signInErr;
    session = signInData?.session || null;
    if (!session) {
      throw new Error('Account created — please log in with your phone number and password.');
    }
  }

  const token = session.access_token;
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  // Self-heal: guarantee the profile row exists BEFORE we enrich it. The signup DB trigger
  // normally creates it, but if that trigger fails the row is missing — the enrich PUT below
  // would then 404 (dropping the typed columns) and the app would strand the seeker on
  // /role-selection. ensure-profile is idempotent (no-op if the profile already exists, e.g.
  // the phone path which creates it server-side).
  try {
    await api.post('/api/auth/ensure-profile', {}, authHeader);
  } catch (err) {
    console.warn('[PESO] ensure-profile failed (non-fatal):', err);
  }

  // The trigger creates the profile asynchronously; give it a beat, then enrich the typed
  // columns it doesn't map. Best-effort with one retry — never block signup.
  if (typedProfile && Object.keys(typedProfile).length > 0) {
    let applied = false;
    for (let attempt = 0; attempt < 2 && !applied; attempt++) {
      try {
        await api.put('/api/profile', typedProfile, authHeader);
        applied = true;
      } catch (err) {
        if (attempt === 0) {
          await sleep(1200); // profile row may not exist yet
        } else {
          console.warn('[PESO] profile enrich failed (non-fatal):', err);
        }
      }
    }
  }

  if (resumeFile) {
    try {
      await uploadResume(resumeFile);
    } catch (err) {
      console.warn('[PESO] resume upload failed (non-fatal):', err);
    }
  }

  return { needsEmailVerification: false };
}
